import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IoArrowBackOutline,
  IoCalendarOutline,
  IoImagesOutline,
  IoTimeOutline,
} from "react-icons/io5";
import api from "../../../configs/api";

/* ─── Helpers ─── */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDescription = (text) => {
  if (!text) return null;
  return text.split("\n").map((para, i) =>
    para.trim() ? (
      <p key={i} className="mb-5 text-gray-700 leading-[1.85] text-[15px]">
        {para}
      </p>
    ) : (
      <br key={i} />
    )
  );
};

const readingTime = (text = "") => {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

/* ─── Related Blog Card ─── */
const RelatedBlogCard = ({ blog, onClick }) => {
  const image = blog.images?.[0];
  return (
    <div
      onClick={() => onClick(blog._id)}
      className="group flex gap-3 cursor-pointer p-3 hover:bg-gray-50 border border-transparent hover:border-borderLight transition-all duration-200"
    >
      {/* Thumbnail */}
      <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-gray-100">
        {image ? (
          <img
            src={image}
            alt={blog.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <IoImagesOutline size={20} className="text-gray-400" />
          </div>
        )}
      </div>

      {/* Text */}
      <div className="flex flex-col justify-center gap-1 min-w-0">
        <h4 className="text-sm font-semibold text-headingText leading-snug line-clamp-2 group-hover:text-primaryAccent transition-colors">
          {blog.name}
        </h4>
        <p className="text-[11px] text-descriptionText line-clamp-1">
          {blog.description}
        </p>
        <span className="text-[11px] text-descriptionText flex items-center gap-1 mt-0.5">
          <IoCalendarOutline size={11} />
          {formatDate(blog.createdAt)}
        </span>
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogDetails();
  }, [id]);

  const fetchBlogDetails = async () => {
    setLoading(true);
    setError("");
    setSelectedImageIndex(0);
    try {
      // Fetch current blog
      const res = await api.get(`/blogs/${id}`);
      const blogData =
        res.data?.data?.blog || res.data?.data || res.data;

      if (blogData && blogData._id) {
        setBlog(blogData);
      } else {
        setError("Blog not found");
        return;
      }

      // Fetch related blogs (all blogs, exclude current)
      const allRes = await api.get(`/blogs?limit=6`);
      const allData = allRes.data?.data;
      const allBlogs = Array.isArray(allData)
        ? allData
        : allData?.blogs ?? [];

      setRelatedBlogs(allBlogs.filter((b) => b._id !== (blogData?._id ?? id)));
    } catch (err) {
      console.error("Error fetching blog:", err);
      if (err.response?.status === 404) setError("Blog not found");
      else setError("Failed to load blog. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <BlogDetailsSkeleton />;

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="text-6xl mb-4">📖</div>
          <h2 className="text-2xl font-bold text-headingText mb-2">
            {error || "Blog not found"}
          </h2>
          <p className="text-descriptionText mb-6">
            The blog you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/blog")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-btnColor text-white hover:bg-btnHoverColor transition"
          >
            <IoArrowBackOutline /> Back to Blogs
          </button>
        </div>
      </div>
    );
  }

  const images = blog.images?.filter(Boolean) || [];
  const hasMultipleImages = images.length > 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero ── */}
      <div className="relative w-full h-[45vh] md:h-[55vh] overflow-hidden bg-gray-900">
        {images.length > 0 ? (
          <>
            <img
              src={images[selectedImageIndex]}
              alt={blog.name}
              className="w-full h-full object-cover object-center transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
            <IoImagesOutline size={80} className="text-white/20" />
          </div>
        )}

        {/* Back button */}
        <button
          onClick={() => navigate("/blog")}
          className="absolute top-6 left-6 z-10 flex items-center gap-2 bg-white/15 backdrop-blur-sm hover:bg-white/25 text-white px-4 py-2 border border-white/20 transition text-sm"
        >
          <IoArrowBackOutline size={16} /> Back to Blogs
        </button>

        {/* Hero title */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8 md:px-10">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl drop-shadow-lg">
              {blog.name}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-white/70 text-sm">
              {blog.createdAt && (
                <span className="flex items-center gap-1.5">
                  <IoCalendarOutline size={14} />
                  {formatDate(blog.createdAt)}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <IoTimeOutline size={14} />
                {readingTime(blog.description)} min read
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Image Thumbnails ── */}
      {hasMultipleImages && (
        <div className="bg-white border-b border-borderLight">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-3">
            <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-1">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`flex-shrink-0 w-16 h-16 overflow-hidden border-2 transition-all ${
                    selectedImageIndex === idx
                      ? "border-primaryAccent shadow-md scale-105"
                      : "border-transparent hover:border-borderLight"
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumb ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Two Column Layout ── */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* ── LEFT: Blog Content ── */}
          <div className="flex-1 min-w-0">
            <div className="bg-white border border-borderLight p-6 md:p-10">
              {/* Article header */}
              <div className="pb-6 mb-6 border-b border-borderLight">
                <h2 className="text-xl md:text-2xl font-bold text-headingText leading-snug">
                  {blog.name}
                </h2>
                <div className="flex items-center gap-4 mt-2 text-xs text-descriptionText">
                  {blog.createdAt && (
                    <span className="flex items-center gap-1">
                      <IoCalendarOutline size={12} />
                      {formatDate(blog.createdAt)}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <IoTimeOutline size={12} />
                    {readingTime(blog.description)} min read
                  </span>
                </div>
              </div>

              {/* Article body */}
              <div className="prose-custom">
                {blog.description ? (
                  formatDescription(blog.description)
                ) : (
                  <p className="text-gray-400 italic">No description provided.</p>
                )}
              </div>

              {/* Footer */}
              <div className="mt-10 pt-6 border-t border-borderLight flex items-center justify-between flex-wrap gap-3">
                <span className="text-xs text-descriptionText">
                  Published {formatDate(blog.createdAt)}
                </span>
                <button
                  onClick={() => navigate("/blog")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-btnColor text-white text-sm hover:bg-btnHoverColor transition"
                >
                  <IoArrowBackOutline size={15} /> All Blogs
                </button>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Related Blogs Sidebar ── */}
          <div className="w-full lg:w-[320px] xl:w-[360px] flex-shrink-0">
            <div className="sticky top-6">
              {/* Related blogs panel */}
              <div className="bg-white border border-borderLight">
                {/* Sidebar header */}
                <div className="px-4 py-3 border-b border-borderLight">
                  <h3 className="text-sm font-semibold text-headingText tracking-wide">
                    Related Blogs
                  </h3>
                </div>

                {relatedBlogs.length > 0 ? (
                  <div className="divide-y divide-borderLight/60">
                    {relatedBlogs.map((rb) => (
                      <RelatedBlogCard
                        key={rb._id}
                        blog={rb}
                        onClick={(blogId) => navigate(`/blog/${blogId}`)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <IoImagesOutline
                      size={28}
                      className="text-descriptionText mx-auto mb-2"
                    />
                    <p className="text-sm text-descriptionText">
                      No related blogs found
                    </p>
                  </div>
                )}
              </div>

              {/* Back CTA card */}
              <div className="mt-4 bg-white border border-borderLight p-4">
                <p className="text-xs text-descriptionText mb-3 leading-relaxed">
                  Explore more articles and stay updated with our latest insights.
                </p>
                <button
                  onClick={() => navigate("/blog")}
                  className="w-full py-2.5 bg-btnColor text-white text-sm font-medium hover:bg-btnHoverColor transition"
                >
                  View All Blogs
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

/* ─── Skeleton ─── */
const BlogDetailsSkeleton = () => (
  <div className="min-h-screen bg-gray-50 animate-pulse">
    {/* Hero skeleton */}
    <div className="w-full h-[45vh] bg-gray-300" />

    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-10">
      <div className="flex flex-col lg:flex-row gap-10">
        {/* Left skeleton */}
        <div className="flex-1 bg-white border border-borderLight p-8">
          <div className="pb-6 mb-6 border-b border-gray-100 space-y-2">
            <div className="h-6 bg-gray-200 rounded w-2/3" />
            <div className="h-3 bg-gray-200 rounded w-1/4" />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className={`h-4 bg-gray-200 rounded ${
                  i % 3 === 2 ? "w-4/5" : "w-full"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Right skeleton */}
        <div className="w-full lg:w-[320px] flex-shrink-0">
          <div className="bg-white border border-borderLight">
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="h-4 bg-gray-200 rounded w-32" />
            </div>
            <div className="divide-y divide-gray-100">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex gap-3 p-3">
                  <div className="w-20 h-20 bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-3 bg-gray-200 rounded w-full" />
                    <div className="h-3 bg-gray-200 rounded w-5/6" />
                    <div className="h-2.5 bg-gray-200 rounded w-1/2 mt-2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default BlogDetails;